import { useCallback, useEffect, useRef, useState } from "react";

type Language = "en" | "kh";
type PaymentCurrency = "USD" | "Riel";
type ApiPaymentCurrency = PaymentCurrency | "usd" | "riel" | "RIEL" | "Riels";

type PaymentTotals = {
  USD: number;
  Riel: number;
};

type PaymentRow = {
  id: number;
  goodsName: string;
  quantity: string;
  unitPrice: string;
  currency: PaymentCurrency;
  amount: number;
};

type PaymentPackage = {
  id: number;
  name: string;
  rows: PaymentRow[];
  totals: PaymentTotals;
};

type PaymentPackageSectionProps = {
  language: Language;
  formatCurrency: (value: number) => string;
  isActive?: boolean;
  onSessionExpired?: () => void;
};

type ApiPaymentItem = {
  id: number;
  package_id: number;
  name_of_goods: string;
  quantity: string | number;
  unit_price: string | number;
  currency: ApiPaymentCurrency;
  amount?: string | number;
};

type ApiPaymentPackage = {
  id: number;
  name: string;
  items: ApiPaymentItem[];
  totals?: {
    usd?: string | number;
    riel?: string | number;
  };
};

type ApiPaymentListResponse = {
  packages: ApiPaymentPackage[];
};

type ApiPaymentItemMutationResponse =
  | {
      items: ApiPaymentItem[];
      package_totals?: {
        usd?: string | number;
        riel?: string | number;
      };
    }
  | ApiPaymentItem;

const API_PAYMENTS_PATH =
  process.env.NEXT_PUBLIC_WEDDING_PAYMENTS_PATH ?? "/wedding/payments";
const WEDDING_API_BASE =
  process.env.NEXT_PUBLIC_WEDDING_API_BASE?.trim().replace(/\/$/, "") || "/api";

const buildApiUrl = (path: string) =>
  `${WEDDING_API_BASE}${path.startsWith("/") ? path : `/${path}`}`;

const getToken = () =>
  typeof window === "undefined" ? null : localStorage.getItem("wedding_access_token");

const toNumber = (value: string | number | null | undefined) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeCurrency = (value: ApiPaymentCurrency | null | undefined): PaymentCurrency => {
  if (typeof value === "string" && value.toLowerCase() === "riel") {
    return "Riel";
  }
  if (typeof value === "string" && value.toLowerCase() === "riels") {
    return "Riel";
  }
  return "USD";
};

const toApiCurrency = (value: PaymentCurrency) => (value === "Riel" ? "Riel" : "USD");

const emptyTotals = (): PaymentTotals => ({ USD: 0, Riel: 0 });

const mapTotals = (
  totals: { usd?: string | number; riel?: string | number } | undefined,
): PaymentTotals => ({
  USD: toNumber(totals?.usd),
  Riel: toNumber(totals?.riel),
});

const mapPaymentRow = (item: ApiPaymentItem): PaymentRow => {
  const quantity = String(item.quantity ?? "");
  const unitPrice = String(item.unit_price ?? "");
  const currency = normalizeCurrency(item.currency);
  return {
    id: item.id,
    goodsName: item.name_of_goods ?? "",
    quantity,
    unitPrice,
    currency,
    amount:
      item.amount === undefined
        ? toNumber(quantity) * toNumber(unitPrice)
        : toNumber(item.amount),
  };
};

const mapPaymentPackage = (item: ApiPaymentPackage): PaymentPackage => ({
  id: item.id,
  name: item.name,
  rows: (item.items ?? []).map(mapPaymentRow),
  totals: mapTotals(item.totals),
});

const hasPaymentItemsArray = (
  payload: ApiPaymentItemMutationResponse,
): payload is Extract<ApiPaymentItemMutationResponse, { items: ApiPaymentItem[] }> =>
  "items" in payload;

const recalculatePackageTotals = (rows: PaymentRow[]): PaymentTotals =>
  rows.reduce((totals, row) => {
    totals[row.currency] += row.amount;
    return totals;
  }, emptyTotals());

const getLocalizedPackageName = (name: string, language: Language) => {
  const englishMatch = /^Payment\s+(\d+)$/i.exec(name.trim());
  if (englishMatch) {
    return language === "kh" ? `ការទូទាត់ ${englishMatch[1]}` : `Payment ${englishMatch[1]}`;
  }

  const khmerMatch = /^ការទូទាត់\s+(\d+)$/i.exec(name.trim());
  if (khmerMatch) {
    return language === "kh" ? `ការទូទាត់ ${khmerMatch[1]}` : `Payment ${khmerMatch[1]}`;
  }

  return name;
};

export default function PaymentPackageSection({
  language,
  formatCurrency,
  isActive = true,
  onSessionExpired,
}: PaymentPackageSectionProps) {
  const [packageNameInput, setPackageNameInput] = useState("");
  const [packages, setPackages] = useState<PaymentPackage[]>([]);
  const [activePackageId, setActivePackageId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingPackage, setIsCreatingPackage] = useState(false);
  const [isAddingRow, setIsAddingRow] = useState(false);
  const [busyPackageId, setBusyPackageId] = useState<number | null>(null);
  const [busyRowId, setBusyRowId] = useState<number | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const activePackageNameInputRef = useRef<HTMLInputElement | null>(null);

  const formatAmountByCurrency = (value: number, currency: PaymentCurrency) => {
    if (currency === "Riel") {
      return `${new Intl.NumberFormat("km-KH").format(value)} ៛`;
    }
    return formatCurrency(value);
  };

  const activePackage =
    packages.find((item) => item.id === activePackageId) ?? packages[0] ?? null;

  const summaryCount = activePackage?.rows.length ?? 0;
  const summaryTotals = activePackage?.totals ?? emptyTotals();

  const createDefaultPackageName = (nextId: number) =>
    language === "kh" ? `បង់ទី${nextId}` : `Payment ${nextId}`;

  const clearAuthSession = useCallback(() => {
    if (typeof window === "undefined") return;
    localStorage.removeItem("wedding_access_token");
    localStorage.removeItem("wedding_token_type");
    localStorage.removeItem("wedding_token_expires_in");
    localStorage.removeItem("wedding_token_expires_at");
  }, []);

  const request = useCallback(async (path: string, init?: RequestInit) => {
    const token = getToken();
    if (!token) {
      clearAuthSession();
      onSessionExpired?.();
      throw new Error(
        language === "kh"
          ? "សម័យចូលប្រើបានផុតកំណត់។ សូមចូលម្ដងទៀត។"
          : "Session expired. Please sign in again.",
      );
    }

    const headers = new Headers(init?.headers);
    if (!headers.has("accept")) {
      headers.set("accept", "application/json");
    }
    headers.set("Authorization", `Bearer ${token}`);

    if (!(init?.body instanceof FormData) && init?.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    const response = await fetch(buildApiUrl(path), {
      ...init,
      headers,
    });

    if (response.status === 401 || response.status === 403) {
      clearAuthSession();
      onSessionExpired?.();
      throw new Error(
        language === "kh"
          ? "សម័យចូលប្រើបានផុតកំណត់។ សូមចូលម្ដងទៀត។"
          : "Session expired. Please sign in again.",
      );
    }

    if (!response.ok) {
      let message =
        language === "kh"
          ? "មិនអាចដំណើរការទិន្នន័យការទូទាត់បានទេ។"
          : "Unable to process payment data.";
      try {
        const payload = (await response.json()) as { message?: string };
        if (payload.message) {
          message = payload.message;
        }
      } catch {
        try {
          const fallbackText = await response.text();
          if (fallbackText.trim()) {
            message = fallbackText.trim();
          }
        } catch {
          // Keep generic message.
        }
      }
      throw new Error(message);
    }

    return response;
  }, [clearAuthSession, language, onSessionExpired]);

  const replacePackage = (nextPackage: PaymentPackage) => {
    setPackages((prev) => prev.map((item) => (item.id === nextPackage.id ? nextPackage : item)));
  };

  const updatePaymentRowLocal = (
    packageId: number,
    rowId: number,
    field: keyof Pick<PaymentRow, "goodsName" | "quantity" | "unitPrice" | "currency">,
    value: string | PaymentCurrency,
  ) => {
    setPackages((prev) =>
      prev.map((item) => {
        if (item.id !== packageId) return item;
        const rows = item.rows.map((row) => {
          if (row.id !== rowId) return row;
          const nextRow = {
            ...row,
            [field]: value,
          } as PaymentRow;
          return {
            ...nextRow,
            amount: toNumber(nextRow.quantity) * toNumber(nextRow.unitPrice),
          };
        });

        return { ...item, rows, totals: recalculatePackageTotals(rows) };
      }),
    );
  };

  const loadPayments = useCallback(async () => {
    if (!isActive) return;
    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await request(API_PAYMENTS_PATH, { method: "GET" });
      const payload = (await response.json()) as ApiPaymentListResponse;
      const nextPackages = (payload.packages ?? []).map(mapPaymentPackage);
      setPackages(nextPackages);
      setActivePackageId((current) => {
        if (current && nextPackages.some((item) => item.id === current)) {
          return current;
        }
        return nextPackages[0]?.id ?? null;
      });
    } catch (error) {
      setPackages([]);
      setActivePackageId(null);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : language === "kh"
            ? "មិនអាចទាញយកកញ្ចប់ទូទាត់បានទេ។"
            : "Unable to load payment packages.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [isActive, language, request]);

  useEffect(() => {
    if (!isActive) return;
    void loadPayments();
  }, [isActive, loadPayments]);

  const handleCreatePackage = async () => {
    const nextId = (packages.at(-1)?.id ?? 0) + 1;
    const nextName = packageNameInput.trim() || createDefaultPackageName(nextId);

    setIsCreatingPackage(true);
    setErrorMessage("");
    try {
      const response = await request(`${API_PAYMENTS_PATH}/packages`, {
        method: "POST",
        body: JSON.stringify({ name: nextName }),
      });
      const payload = (await response.json()) as ApiPaymentPackage;
      const nextPackage = mapPaymentPackage(payload);
      setPackages((prev) => [...prev, nextPackage]);
      setActivePackageId(nextPackage.id);
      setPackageNameInput("");
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : language === "kh"
            ? "មិនអាចបង្កើតកញ្ចប់ទូទាត់បានទេ។"
            : "Unable to create payment package.",
      );
    } finally {
      setIsCreatingPackage(false);
    }
  };

  const handleRenamePackage = async (packageId: number, name: string) => {
    const trimmedName = name.trim();
    const current = packages.find((item) => item.id === packageId);
    if (!current || current.name === trimmedName || trimmedName.length === 0) {
      if (current && trimmedName.length === 0) {
        replacePackage({ ...current, name: current.name });
      }
      return;
    }

    setBusyPackageId(packageId);
    setErrorMessage("");
    try {
      const response = await request(`${API_PAYMENTS_PATH}/packages/${packageId}`, {
        method: "PATCH",
        body: JSON.stringify({ name: trimmedName }),
      });
      const payload = (await response.json()) as ApiPaymentPackage;
      replacePackage(mapPaymentPackage(payload));
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : language === "kh"
            ? "មិនអាចកែឈ្មោះរាយផលិតផលបានទេ។"
            : "Unable to rename package.",
      );
      await loadPayments();
    } finally {
      setBusyPackageId(null);
    }
  };

  const handleDeletePackage = async () => {
    if (!activePackage) return;

    setBusyPackageId(activePackage.id);
    setErrorMessage("");
    try {
      await request(`${API_PAYMENTS_PATH}/packages/${activePackage.id}`, {
        method: "DELETE",
      });
      const remaining = packages.filter((item) => item.id !== activePackage.id);
      setPackages(remaining);
      setActivePackageId((current) =>
        current === activePackage.id ? remaining[0]?.id ?? null : current,
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : language === "kh"
            ? "មិនអាចលុបកញ្ចប់ទូទាត់បានទេ។"
            : "Unable to delete payment package.",
      );
    } finally {
      setBusyPackageId(null);
    }
  };

  const addPaymentRow = async () => {
    if (!activePackage) return;

    setIsAddingRow(true);
    setErrorMessage("");
    try {
      const response = await request(
        `${API_PAYMENTS_PATH}/packages/${activePackage.id}/items`,
        {
          method: "POST",
          body: JSON.stringify({
            name_of_goods: "",
            quantity: 0,
            unit_price: 0,
            currency: "USD",
          }),
        },
      );
      const payload = (await response.json()) as ApiPaymentItemMutationResponse;
      const createdItems = hasPaymentItemsArray(payload) ? payload.items : [payload];
      const appendedRows = createdItems.map(mapPaymentRow);

      setPackages((prev) =>
        prev.map((item) =>
          item.id === activePackage.id
            ? {
                ...item,
                rows: [...item.rows, ...appendedRows],
                totals:
                  hasPaymentItemsArray(payload) && payload.package_totals
                    ? mapTotals(payload.package_totals)
                    : item.totals,
              }
            : item,
        ),
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : language === "kh"
            ? "មិនអាចបន្ថែមជួរបានទេ។"
            : "Unable to add payment row.",
      );
    } finally {
      setIsAddingRow(false);
    }
  };

  const persistPaymentRow = async (
    packageId: number,
    rowId: number,
    overrides?: Partial<Pick<PaymentRow, "goodsName" | "quantity" | "unitPrice" | "currency">>,
  ) => {
    const sourcePackage = packages.find((item) => item.id === packageId);
    const row = sourcePackage?.rows.find((item) => item.id === rowId);
    if (!row) return;
    const nextRow = { ...row, ...overrides };

    setBusyRowId(rowId);
    setErrorMessage("");
    try {
      const response = await request(`${API_PAYMENTS_PATH}/items/${rowId}`, {
        method: "PATCH",
        body: JSON.stringify({
          name_of_goods: nextRow.goodsName,
          quantity: toNumber(nextRow.quantity),
          unit_price: toNumber(nextRow.unitPrice),
          currency: toApiCurrency(nextRow.currency),
        }),
      });
      const payload = (await response.json()) as ApiPaymentItem & {
        package_totals?: {
          usd?: string | number;
          riel?: string | number;
        };
      };
      const mappedRow = mapPaymentRow(payload);
      setPackages((prev) =>
        prev.map((item) => {
          if (item.id !== packageId) return item;
          return {
            ...item,
            rows: item.rows.map((currentRow) =>
              currentRow.id === rowId ? mappedRow : currentRow,
            ),
            totals: payload.package_totals ? mapTotals(payload.package_totals) : item.totals,
          };
        }),
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : language === "kh"
            ? "មិនអាចរក្សាទុកជួរទូទាត់បានទេ។"
            : "Unable to save payment row.",
      );
      await loadPayments();
    } finally {
      setBusyRowId(null);
    }
  };

  const deletePaymentRow = async (rowId: number) => {
    if (!activePackage) return;

    setBusyRowId(rowId);
    setErrorMessage("");
    try {
      await request(`${API_PAYMENTS_PATH}/items/${rowId}`, {
        method: "DELETE",
      });
      setPackages((prev) =>
        prev.map((item) => {
          if (item.id !== activePackage.id) return item;
          const rows = item.rows.filter((row) => row.id !== rowId);
          const totals = recalculatePackageTotals(rows);
          return { ...item, rows, totals };
        }),
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : language === "kh"
            ? "មិនអាចលុបជួរបានទេ។"
            : "Unable to delete payment row.",
      );
    } finally {
      setBusyRowId(null);
    }
  };

  const handleDownloadExcel = async () => {
    setErrorMessage("");
    try {
      const response = await request(`${API_PAYMENTS_PATH}/export`, {
        method: "GET",
        headers: {
          accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*",
        },
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers.get("content-disposition");
      const fileNameMatch = disposition?.match(/filename="?([^"]+)"?/i);

      link.href = url;
      link.download = fileNameMatch?.[1] ?? "payment-packages.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : language === "kh"
            ? "មិនអាចទាញយកឯកសារ Excel បានទេ។"
            : "Unable to download Excel file.",
      );
    }
  };

  return (
    <div className="mt-2 flex h-[calc(100svh-7rem)] flex-col gap-4 overflow-hidden sm:mt-4 sm:h-[calc(100svh-7.5rem)] sm:gap-6">
      <div className="shrink-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_16px_38px_rgba(51,65,85,0.12)] dark:border-[color:var(--field-border)] dark:bg-[color:var(--surface)] dark:shadow-[0_18px_42px_rgba(0,0,0,0.3)]">
        <div className="border-b border-slate-200 bg-white px-5 py-4 dark:border-[color:var(--divider)] dark:bg-[color:var(--surface-strong)]">
          <h2 className="font-[family-name:var(--font-display)] text-2xl font-bold leading-tight text-slate-950 dark:text-white sm:text-3xl">
            {language === "kh" ? "កញ្ចប់ទូទាត់" : "Payment Package"}
          </h2>
        </div>

        <div className="bg-white p-4 dark:bg-[color:var(--surface)] sm:p-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 shadow-sm dark:border-[color:var(--field-border)] dark:bg-[color:var(--surface-strong)]">
            <label className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-700 dark:text-slate-200">
              {language === "kh" ? "ឈ្មោះរាយផលិតផលថ្មី" : "New package name"}
            </label>
            <form
              className="mt-2 flex flex-col gap-2 sm:flex-row"
              onSubmit={(event) => {
                event.preventDefault();
                void handleCreatePackage();
              }}
            >
              <input
                value={packageNameInput}
                onChange={(event) => setPackageNameInput(event.target.value)}
                placeholder={language === "kh" ? "ឧទាហរណ៍: បង់ទី២" : "Example: Payment 2"}
                className="flex-1 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 outline-none transition placeholder:font-medium placeholder:text-slate-400 focus:border-[#8f6b3f] focus:ring-2 focus:ring-amber-200/70 dark:border-slate-700 dark:bg-[color:var(--surface)] dark:text-white dark:placeholder:text-slate-400 dark:focus:ring-amber-400/15"
              />
              <button
                type="submit"
                data-tab-trigger="true"
                disabled={isCreatingPackage}
                className="inline-flex items-center justify-center rounded-xl border border-[#79572f] bg-[#8f6b3f] px-5 py-2.5 text-sm font-bold !text-white shadow-sm transition hover:bg-[#79572f] disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-200 disabled:!text-slate-500 dark:border-[#b88a38] dark:bg-[#8f6b3f] dark:!text-white dark:hover:bg-[#79572f] dark:disabled:border-slate-700 dark:disabled:bg-slate-800 dark:disabled:!text-slate-500"
              >
                {isCreatingPackage
                  ? language === "kh"
                    ? "កំពុងបង្កើត..."
                    : "Creating..."
                  : language === "kh"
                    ? "បង្កើតឈ្មោះរាយទំនិញ"
                    : "Create package"}
              </button>
            </form>
          </div>

          {errorMessage ? (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 dark:border-red-500/35 dark:bg-red-500/12 dark:text-red-200">
              {errorMessage}
            </div>
          ) : null}
        </div>

        <div className="grid divide-y divide-slate-200 border-t border-slate-200 md:grid-cols-[1.2fr_0.8fr_1fr] md:divide-x md:divide-y-0 dark:divide-[color:var(--divider)] dark:border-[color:var(--divider)]">
          <div className="bg-white dark:bg-[color:var(--surface)]">
            <div className="bg-slate-50 px-5 py-2.5 dark:bg-[color:var(--surface-strong)]">
              <span className="inline-flex rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700 shadow-sm dark:border-slate-600 dark:bg-[color:var(--surface)] dark:text-slate-200">
                {language === "kh" ? "វិក័យបត្របង់ប្រាក់" : "Active package"}
              </span>
            </div>
            <div className="px-5 py-4">
              <p className="line-clamp-2 text-base font-bold leading-6 text-slate-950 dark:text-white">
                {activePackage ? getLocalizedPackageName(activePackage.name, language) : "-"}
              </p>
            </div>
          </div>
          <div className="bg-white dark:bg-[color:var(--surface)]">
            <div className="bg-slate-50 px-5 py-2.5 dark:bg-[color:var(--surface-strong)]">
              <span className="inline-flex rounded-lg border border-[#d8c299] bg-[#fff8e8] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#79572f] shadow-sm dark:border-amber-400/35 dark:bg-amber-400/15 dark:text-amber-100">
                {language === "kh" ? "ចំនួនជួរទូទាត់" : "Payment rows"}
              </span>
            </div>
            <div className="px-5 py-4">
              <p className="text-2xl font-bold leading-none text-slate-950 dark:text-amber-200">
                {summaryCount}
              </p>
            </div>
          </div>
          <div className="bg-white dark:bg-[color:var(--surface)]">
            <div className="bg-slate-50 px-5 py-2.5 dark:bg-[color:var(--surface-strong)]">
              <span className="inline-flex rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-700 shadow-sm dark:border-slate-600 dark:bg-[color:var(--surface)] dark:text-slate-200">
                {language === "kh" ? "ចំនួនសរុប" : "Package total"}
              </span>
            </div>
            <div className="divide-y divide-slate-200 dark:divide-[color:var(--divider)]">
              <p className="px-5 py-2.5 text-base font-bold text-[#2f6b45] dark:text-emerald-300">
                {formatAmountByCurrency(summaryTotals.USD, "USD")}
              </p>
              <p className="px-5 py-2.5 text-sm font-bold text-slate-800 dark:text-slate-200">
                {formatAmountByCurrency(summaryTotals.Riel, "Riel")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_18px_46px_rgba(51,65,85,0.12)] dark:border-[color:var(--panel-border)] dark:bg-[color:var(--card)] dark:shadow-[var(--panel-shadow)] sm:p-6">
        <div className="flex flex-col gap-4 border-b border-slate-200 pb-4 dark:border-[color:var(--field-border)]">
          <div className="flex flex-wrap gap-2">
            {packages.map((item) => {
              const isActive = item.id === activePackageId;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-tab-trigger="true"
                  onClick={() => setActivePackageId(item.id)}
                  disabled={busyPackageId === item.id}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "bg-[#8f6b3f] !text-white shadow-[0_10px_24px_rgba(143,107,63,0.28)] dark:bg-[color:var(--accent)] dark:!text-slate-950"
                      : "border border-slate-300 bg-white text-slate-800 shadow-sm hover:border-[#b88a38] hover:bg-[#fff8e8] dark:border-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-slate-200 dark:hover:border-[#d6b26a] dark:hover:bg-amber-400/10"
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  {getLocalizedPackageName(item.name, language)}
                </button>
              );
            })}
          </div>

          {activePackage ? (
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="w-full max-w-xl">
                <label className="block text-xs font-bold uppercase tracking-[0.16em] text-slate-700 dark:text-slate-200">
                  {language === "kh" ? "ឈ្មោះរាយផលិតផល" : "Package name"}
                </label>
                <input
                  ref={activePackageNameInputRef}
                  value={activePackage.name}
                  onChange={(event) =>
                    setPackages((prev) =>
                      prev.map((item) =>
                        item.id === activePackage.id
                          ? { ...item, name: event.target.value }
                          : item,
                      ),
                    )
                  }
                  onBlur={(event) => void handleRenamePackage(activePackage.id, event.target.value)}
                  disabled={busyPackageId === activePackage.id}
                  className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-950 outline-none transition focus:border-[#8f6b3f] focus:ring-2 focus:ring-amber-200/70 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-white dark:focus:ring-amber-400/15 dark:disabled:bg-slate-800 dark:disabled:text-slate-500"
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  data-tab-trigger="true"
                  onClick={() => setShowPreviewModal((prev) => !prev)}
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-full transition ${
                    showPreviewModal
                      ? "border border-blue-700 bg-blue-700 text-white shadow-sm hover:bg-blue-800 dark:border-blue-400 dark:bg-blue-500 dark:text-white dark:hover:bg-blue-400"
                      : "border border-blue-200 bg-blue-50 text-blue-700 shadow-sm hover:border-blue-700 hover:bg-blue-700 hover:text-white dark:border-blue-400/30 dark:bg-blue-400/15 dark:text-blue-200 dark:hover:bg-blue-400 dark:hover:text-blue-950"
                  }`}
                  aria-label={language === "kh" ? "មើលទិន្នន័យ" : "Preview"}
                  title={language === "kh" ? "មើលទិន្នន័យ" : "Preview"}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
                    <circle cx="12" cy="12" r="2.5" />
                  </svg>
                </button>
                <button
                  type="button"
                  data-tab-trigger="true"
                  onClick={() => void handleDownloadExcel()}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-green-200 bg-green-50 text-green-700 shadow-sm transition hover:border-green-700 hover:bg-green-700 hover:text-white dark:border-green-400/30 dark:bg-green-400/15 dark:text-green-200 dark:hover:bg-green-400 dark:hover:text-green-950"
                  aria-label={language === "kh" ? "ទាញយក Excel" : "Download Excel"}
                  title={language === "kh" ? "ទាញយក Excel" : "Download Excel"}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 3v12" />
                    <path d="m7 10 5 5 5-5" />
                    <path d="M5 21h14" />
                  </svg>
                </button>
                <button
                  type="button"
                  data-tab-trigger="true"
                  onClick={() => {
                    activePackageNameInputRef.current?.focus();
                    activePackageNameInputRef.current?.select();
                  }}
                  disabled={busyPackageId === activePackage.id}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-violet-300 bg-violet-100 text-violet-800 shadow-sm transition hover:border-violet-700 hover:bg-violet-700 hover:!text-white disabled:cursor-not-allowed disabled:opacity-60 dark:border-violet-400/30 dark:bg-violet-400/15 dark:text-violet-200 dark:hover:bg-violet-400 dark:hover:!text-violet-950"
                  aria-label={language === "kh" ? "កែឈ្មោះរាយផលិតផល" : "Edit package"}
                  title={language === "kh" ? "កែឈ្មោះរាយផលិតផល" : "Edit package"}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                </button>
                <button
                  type="button"
                  data-tab-trigger="true"
                  onClick={() => void addPaymentRow()}
                  disabled={isAddingRow}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-500 !text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-amber-500 dark:!text-white dark:hover:bg-amber-400"
                  aria-label={language === "kh" ? "បន្ថែមជួរ" : "Add row"}
                  title={language === "kh" ? "បន្ថែមជួរ" : "Add row"}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 5v14" />
                    <path d="M5 12h14" />
                  </svg>
                </button>
                <button
                  type="button"
                  data-tab-trigger="true"
                  onClick={() => void handleDeletePackage()}
                  disabled={busyPackageId === activePackage.id}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-600 !text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-red-500 dark:!text-white dark:hover:bg-red-400"
                  aria-label={language === "kh" ? "លុបឈ្មោះរាយទំនិញ" : "Delete package"}
                  title={language === "kh" ? "លុបឈ្មោះរាយទំនិញ" : "Delete package"}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 6h18" />
                    <path d="M8 6V4.75A1.75 1.75 0 0 1 9.75 3h4.5A1.75 1.75 0 0 1 16 4.75V6" />
                    <path d="M19 6l-1 13.25A1.75 1.75 0 0 1 16.26 21H7.74A1.75 1.75 0 0 1 6 19.25L5 6" />
                    <path d="M10 10.5v6" />
                    <path d="M14 10.5v6" />
                  </svg>
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {showPreviewModal && activePackage ? (
          <div className="mt-4 min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-[color:var(--field-border)] dark:bg-[color:var(--surface)]">
            <div className="h-full overflow-x-auto overflow-y-auto overscroll-contain">
              <table className="min-w-[820px] w-full text-left text-sm">
                <thead className="text-xs font-bold uppercase tracking-[0.14em] text-slate-700 dark:text-slate-200">
                  <tr>
                    <th className="bg-slate-100 px-4 py-3 font-bold text-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-slate-200">
                      {language === "kh" ? "ល.រ" : "No"}
                    </th>
                    <th className="bg-slate-100 px-4 py-3 font-bold text-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-slate-200">
                      {language === "kh" ? "ឈ្មោះទំនិញ" : "Name of Goods"}
                    </th>
                    <th className="bg-slate-100 px-4 py-3 font-bold text-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-slate-200">
                      {language === "kh" ? "ចំនួន" : "Quantity"}
                    </th>
                    <th className="bg-slate-100 px-4 py-3 font-bold text-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-slate-200">
                      {language === "kh" ? "តម្លៃឯកតា" : "Unit Price"}
                    </th>
                    <th className="bg-slate-100 px-4 py-3 font-bold text-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-slate-200">
                      {language === "kh" ? "ចំនួនទឹកប្រាក់" : "Amount"}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-[color:var(--divider)]">
                  {activePackage.rows.length > 0 ? (
                    activePackage.rows.map((row, index) => (
                      <tr key={`preview-${row.id}`} className="transition hover:bg-slate-50 dark:hover:bg-white/[0.025]">
                        <td className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-400">
                          {index + 1}
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-950 dark:text-slate-100">
                          {row.goodsName || (language === "kh" ? "ឈ្មោះទំនិញ" : "Goods name")}
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-800 dark:text-slate-200">
                          {row.quantity || "0"}
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-800 dark:text-slate-200">
                          {formatAmountByCurrency(toNumber(row.unitPrice), row.currency)}
                        </td>
                        <td className="px-4 py-3 font-bold text-[#2f6b45] dark:text-emerald-300">
                          {formatAmountByCurrency(row.amount, row.currency)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-sm font-medium text-slate-600 dark:text-slate-400">
                        {language === "kh"
                          ? "មិនទាន់មានជួរទូទាត់នៅក្នុងឈ្មោះរាយទំនិញនេះទេ។"
                          : "There are no payment rows in this package yet."}
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="bg-white/80 text-sm font-semibold dark:bg-[color:var(--surface-strong)]">
                  <tr>
                    <td colSpan={4} />
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2 text-right">
                        <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-700 dark:text-slate-300">
                          {language === "kh" ? "សរុប:" : "Total:"}
                        </span>
                        <span className="text-sm font-bold text-[#2f6b45] dark:text-emerald-300">
                          {formatAmountByCurrency(activePackage.totals.USD, "USD")}
                        </span>
                        <span className="text-xs font-medium text-slate-400 dark:text-slate-500">•</span>
                        <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                          {formatAmountByCurrency(activePackage.totals.Riel, "Riel")}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ) : null}

        {!showPreviewModal ? (
          <div className="mt-4 min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-[color:var(--field-border)] dark:bg-[color:var(--surface)]">
            <div className="h-full overflow-x-auto overflow-y-auto overscroll-contain">
              <table className="min-w-[920px] w-full text-left text-sm">
                <thead className="text-xs font-bold uppercase tracking-[0.14em] text-slate-700 dark:text-slate-200">
                  <tr>
                    <th className="sticky top-0 z-10 bg-slate-100 px-4 py-3 font-bold text-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-slate-200">
                      {language === "kh" ? "ល.រ" : "No"}
                    </th>
                    <th className="sticky top-0 z-10 bg-slate-100 px-4 py-3 font-bold text-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-slate-200">
                      {language === "kh" ? "ឈ្មោះទំនិញ" : "Name of Goods"}
                    </th>
                    <th className="sticky top-0 z-10 bg-slate-100 px-4 py-3 font-bold text-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-slate-200">
                      {language === "kh" ? "ចំនួន" : "Quantity"}
                    </th>
                    <th className="sticky top-0 z-10 bg-slate-100 px-4 py-3 font-bold text-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-slate-200">
                      {language === "kh" ? "តម្លៃឯកតា" : "Unit Price"}
                    </th>
                    <th className="sticky top-0 z-10 bg-slate-100 px-4 py-3 font-bold text-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-slate-200">
                      {language === "kh" ? "ចំនួនទឹកប្រាក់" : "Amount"}
                    </th>
                    <th className="sticky top-0 z-10 bg-slate-100 px-4 py-3 text-right font-bold text-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-slate-200">
                      {language === "kh" ? "សកម្មភាព" : "Action"}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-[color:var(--divider)]">
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-sm font-medium text-slate-600 dark:text-slate-400">
                        {language === "kh" ? "កំពុងទាញយក..." : "Loading..."}
                      </td>
                    </tr>
                  ) : activePackage ? (
                    activePackage.rows.length > 0 ? (
                      activePackage.rows.map((row, index) => (
                        <tr key={row.id} className="transition hover:bg-slate-50 dark:hover:bg-white/[0.025]">
                          <td className="px-4 py-3 align-top font-semibold text-slate-700 dark:text-slate-400">
                            {index + 1}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <input
                              value={row.goodsName}
                              onChange={(event) =>
                                updatePaymentRowLocal(
                                  activePackage.id,
                                  row.id,
                                  "goodsName",
                                  event.target.value,
                                )
                              }
                              onBlur={() => void persistPaymentRow(activePackage.id, row.id)}
                            placeholder={language === "kh" ? "ឈ្មោះទំនិញ" : "Goods name"}
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-950 outline-none transition placeholder:font-medium placeholder:text-slate-400 focus:border-[#8f6b3f] focus:ring-2 focus:ring-amber-200/70 dark:border-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-white dark:placeholder:text-slate-400 dark:focus:ring-amber-400/15"
                          />
                        </td>
                        <td className="px-4 py-3 align-top">
                          <input
                            value={row.quantity}
                            onChange={(event) =>
                              updatePaymentRowLocal(
                                activePackage.id,
                                row.id,
                                "quantity",
                                event.target.value.replace(/[^\d.]/g, ""),
                              )
                            }
                            onBlur={() => void persistPaymentRow(activePackage.id, row.id)}
                            placeholder="0"
                            inputMode="decimal"
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-950 outline-none transition placeholder:font-medium placeholder:text-slate-400 focus:border-[#8f6b3f] focus:ring-2 focus:ring-amber-200/70 dark:border-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-white dark:placeholder:text-slate-400 dark:focus:ring-amber-400/15"
                          />
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="flex gap-2">
                            <input
                              value={row.unitPrice}
                              onChange={(event) =>
                                updatePaymentRowLocal(
                                  activePackage.id,
                                  row.id,
                                  "unitPrice",
                                  event.target.value.replace(/[^\d.]/g, ""),
                                )
                              }
                              onBlur={() => void persistPaymentRow(activePackage.id, row.id)}
                              placeholder="0"
                              inputMode="decimal"
                              className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-950 outline-none transition placeholder:font-medium placeholder:text-slate-400 focus:border-[#8f6b3f] focus:ring-2 focus:ring-amber-200/70 dark:border-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-white dark:placeholder:text-slate-400 dark:focus:ring-amber-400/15"
                            />
                            <select
                              value={row.currency}
                              onChange={(event) => {
                                updatePaymentRowLocal(
                                  activePackage.id,
                                  row.id,
                                  "currency",
                                  event.target.value as PaymentCurrency,
                                );
                                void persistPaymentRow(activePackage.id, row.id, {
                                  currency: event.target.value as PaymentCurrency,
                                });
                              }}
                              className="w-24 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-950 outline-none transition focus:border-[#8f6b3f] focus:ring-2 focus:ring-amber-200/70 dark:border-slate-700 dark:bg-[color:var(--surface-strong)] dark:text-white dark:focus:ring-amber-400/15"
                              aria-label={language === "kh" ? "រូបិយប័ណ្ណ" : "Currency"}
                            >
                              <option value="USD">USD</option>
                              <option value="Riel">Riel</option>
                            </select>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="rounded-xl border border-[#bfdac9] bg-[#f2fbf5] px-3 py-2.5 text-sm font-bold text-[#2f6b45] dark:border-green-400/25 dark:bg-green-400/10 dark:text-green-300">
                            {formatAmountByCurrency(row.amount, row.currency)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right align-top">
                          <button
                            type="button"
                            data-tab-trigger="true"
                            onClick={() => void deletePaymentRow(row.id)}
                            disabled={busyRowId === row.id}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-600 !text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-red-500 dark:!text-white dark:hover:bg-red-400"
                            aria-label={language === "kh" ? "លុបជួរ" : "Delete row"}
                            title={language === "kh" ? "លុបជួរ" : "Delete row"}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              className="h-4 w-4"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M3 6h18" />
                              <path d="M8 6V4.75A1.75 1.75 0 0 1 9.75 3h4.5A1.75 1.75 0 0 1 16 4.75V6" />
                              <path d="M19 6l-1 13.25A1.75 1.75 0 0 1 16.26 21H7.74A1.75 1.75 0 0 1 6 19.25L5 6" />
                              <path d="M10 10.5v6" />
                              <path d="M14 10.5v6" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-10">
                        <div className="flex flex-col items-center justify-center gap-3 text-center">
                          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                            {language === "kh"
                              ? "មិនទាន់មានជួរទូទាត់នៅក្នុងឈ្មោះរាយទំនិញនេះទេ។"
                              : "There are no payment rows in this package yet."}
                          </p>
                          <button
                            type="button"
                            data-tab-trigger="true"
                            onClick={() => void addPaymentRow()}
                            disabled={isAddingRow}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-500 !text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-amber-500 dark:!text-white dark:hover:bg-amber-400"
                            aria-label={language === "kh" ? "បន្ថែមជួរ" : "Add row"}
                            title={language === "kh" ? "បន្ថែមជួរ" : "Add row"}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              className="h-5 w-5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M12 5v14" />
                              <path d="M5 12h14" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-12">
                      <div className="flex flex-col items-center justify-center gap-3 text-center">
                        <p className="text-base font-bold text-slate-900 dark:text-white">
                          {language === "kh"
                            ? "មិនទាន់មានកញ្ចប់ទូទាត់ទេ"
                            : "No payment package yet"}
                        </p>
                        <p className="max-w-md text-sm font-medium leading-6 text-slate-600 dark:text-slate-400">
                          {language === "kh"
                            ? "បញ្ចូលឈ្មោះរាយផលិតផលថ្មី ហើយចុចបង្កើត ដើម្បីបង្ហាញកញ្ចប់ទូទាត់សម្រាប់អ្នកប្រើ។"
                            : "Enter a package name and create one to display it for the user automatically."}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50 dark:border-[color:var(--field-border)] dark:bg-[color:var(--surface-strong)]">
                  <td colSpan={4} />
                  <td colSpan={2} className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2 text-right">
                      <span className="text-xs font-bold uppercase tracking-[0.14em] text-slate-700 dark:text-slate-300">
                        {language === "kh" ? "សរុប:" : "Total:"}
                      </span>
                      <span className="text-sm font-bold text-[#2f6b45] dark:text-emerald-300">
                        {formatAmountByCurrency(summaryTotals.USD, "USD")}
                      </span>
                      <span className="text-xs font-medium text-slate-400 dark:text-slate-500">•</span>
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                        {formatAmountByCurrency(summaryTotals.Riel, "Riel")}
                      </span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        ) : null}
      </div>
    </div>
  );
}
